import type { LabelColor } from 'src/components/label';

import { Tooltip } from '@mui/material';

import { Label } from 'src/components/label';

// ----------------------------------------------------------------------

type Props = {
  row: any;
  headCellId: string;
  value: string;
  moduleName?: string;
  t: (key: string) => string;
  baseTabs?: { value: string; label: string }[];
};

// ----------------------------------------------------------------------

export function RowCellStatus({ row, headCellId, value, moduleName, t, baseTabs = [] }: Props) {
  // Logic for 'status', 'accountStatus', 'userStatus', 'labelType'
  if (['status', 'accountStatus', 'userStatus', 'labelType'].includes(headCellId)) {
    const setColor = (): LabelColor => {
      // General modules (tickets, etc.) - excluding conversation, inbox, campaign
      if (
        'status' in row &&
        row.status &&
        !['conversation', 'inbox', 'campaign'].includes(moduleName ?? '')
      ) {
        const rowStatus = row.status.toLowerCase();
        if (['active', 'completed', 'approved', 'answered', 'paid'].includes(rowStatus))
          return 'success';
        if (['pending', 'in progress', 'paused'].includes(rowStatus)) return 'warning';
        if (
          ['banned', 'inactive', 'rejected', 'failed', 'open', 'payment_due', 'disabled'].includes(
            rowStatus
          )
        )
          return 'error';
        if (['on hold'].includes(rowStatus)) return 'info';
        return 'default';
      }

      // Conversation and Inbox modules
      if (
        'status' in row &&
        row.status &&
        ['conversation', 'inbox'].includes(moduleName ?? '') &&
        !['campaign'].includes(moduleName ?? '')
      ) {
        const rowStatus = row.status.toLowerCase();
        if (['active', 'completed', 'approved', 'open'].includes(rowStatus)) return 'success';
        if (['pending', 'in progress'].includes(rowStatus)) return 'warning';
        if (['banned', 'inactive', 'rejected', 'failed', 'close'].includes(rowStatus))
          return 'error';
        if (['on hold'].includes(rowStatus)) return 'info';
        return 'default';
      }

      // Campaign module
      if (
        'status' in row &&
        row.status &&
        !['conversation', 'inbox'].includes(moduleName ?? '') &&
        ['campaign'].includes(moduleName ?? '')
      ) {
        const rowStatus = row.status.toLowerCase();
        if (['read'].includes(rowStatus)) return 'success';
        if (['delivered'].includes(rowStatus)) return 'warning';
        if (['failed'].includes(rowStatus)) return 'error';
        if (['sent'].includes(rowStatus)) return 'info';
        return 'default';
      }

      // Label types
      if ('labelType' in row && row.labelType) {
        const extraTypes = [
          'Campaign',
          'Project',
          'Account',
          'Recruitment',
          'Management',
          'Collaboration',
        ];
        if (row.labelType === 'Contact') return 'success';
        if (row.labelType === 'Ticket') return 'warning';
        if (row.labelType === 'Conversation') return 'error';
        if (extraTypes.includes(row.labelType)) return 'error';
        return 'default';
      }

      return 'default';
    };

    const getLabelByValue = (labelvalue: string): string => {
      const tab = baseTabs.find((tab1) => tab1.value === labelvalue);
      return tab ? tab.label : labelvalue || 'UNKNOWN';
    };

    const rawError =
      row?.errorReason ||
      row?.metadata?.error ||
      row?.metadata?.errorReason ||
      row?.metadata?.reason ||
      row?.error ||
      row?.reason;

    const errorTooltipTitle =
      row?.status && String(row.status).toUpperCase() === 'FAILED' && rawError
        ? typeof rawError === 'string'
          ? rawError
          : rawError.userMessage || rawError.title || rawError.message || JSON.stringify(rawError)
        : '';

    const renderContent = () => {
      if ('status' in row) {
        if (moduleName === 'call' || moduleName === 'callreport') {
          return row?.status;
        }
        if (moduleName === 'campaign') {
          return t(getLabelByValue(row?.status || ''));
        }
        return t(row?.status?.toUpperCase().replace(/ /g, '_'));
      }
      return t(row?.labelType?.toUpperCase().replace(/ /g, '_'));
    };

    const statusBadge = (
      <Label
        variant="soft"
        color={setColor()}
        sx={{
          cursor: errorTooltipTitle ? 'pointer' : 'default',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            transform: 'scale(1.05)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          },
        }}
      >
        {renderContent()}
      </Label>
    );

    if (errorTooltipTitle) {
      return (
        <Tooltip title={errorTooltipTitle} arrow placement="top">
          {statusBadge}
        </Tooltip>
      );
    }

    return statusBadge;
  }

  // Logic for 'priority'
  if (headCellId === 'priority' && 'priority' in row) {
    const priorityColorMap: { [key: string]: string } = {
      normal: 'success',
      low: 'warning',
      high: 'error',
      banned: 'error',
      inactive: 'error',
    };
    return (
      <Label
        variant="soft"
        color={(priorityColorMap[row?.priority?.toLowerCase()] as LabelColor) || 'default'}
        sx={{
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            transform: 'scale(1.05)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          },
        }}
      >
        {t(row?.priority?.toUpperCase())}
      </Label>
    );
  }

  // Logic for 'bill_status'
  if (headCellId === 'bill_status' && 'bill_status' in row) {
    const priorityColorMap: { [key: string]: string } = {
      active: 'success',
      inactive: 'error',
    };

    return (
      <Label
        variant="soft"
        color={(priorityColorMap[value?.toLowerCase()] as LabelColor) || 'default'}
        sx={{
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            transform: 'scale(1.05)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          },
        }}
      >
        {t(value?.toUpperCase())}
      </Label>
    );
  }

  return null;
}
